<template>
    <div class="card">
        <div class="col-12 px-2 mt-2 mb-4">
            <div class="card p-2" style="min-height: 200px">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Patient Name(Patient Id)</th>
                                <th>Gender</th>
                                <th>Mobile No</th>
                                <th>Age</th>
                                <th>Schedule Date</th>
                                <th>Doctor Name</th>
                                <th>Service Name</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(item, i) in tableItems" :key="i">
                                <tr :style="{
                                        backgroundColor: taskStatusColors[item.status].bg || '#f9fafb',
                                        color: taskStatusColors[item.status].text || '#111827',
                                    }"
                                >
                                <td>{{ item.patient_name }}({{ item.patient_serial_no }})</td>
                                <td>{{ convertToTitleCase(item.patient_gender) }}</td>
                                <td>{{ item.patient_mobile_no }}</td>
                                <td>{{ item.patient_age }}</td>
                                <td>{{ item.start_time }}</td>
                                <td>{{ item.doctor_name }}</td>
                                <td>{{ item.product_name }}</td>
                                <td>{{ convertToTitleCase(item.status) }}</td>
                                <td>
                                    <button v-if="isDoctor" @click="goToPrescription(item, 'redirect')" class="btn btn-sm"><i class="fas fa-pencil"></i></button>
                                    <div class="btn-group" v-if="item.service_type === 'treatment_plan' && item.prescription_count > 0">
                                        <button type="button" class="btn btn-sm btn-primary dropdown-toggle hide-arrow" data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="fas fa-print"></i>
                                        </button>
                                        <div class="dropdown-menu dropdown-menu-end">
                                          <a @click="goToPrescription(item, 'print', true)" class="dropdown-item" href="javascript:void(0);">
                                            <i class="fas fa-file-invoice mr-50"></i>
                                            <span> Print</span>
                                          </a>
                                            <a @click="goToPrescription(item, 'print')" class="dropdown-item" href="javascript:void(0);">
                                                <i class="fas fa-print mr-50"></i>
                                                <span> Without Header</span>
                                            </a>
                                        </div>
                                    </div>
                                </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
                </div>
            </div>
        </div>

        <div class="px-2 position-absolute bottom-0 mt-4">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

    </div>
</template>

<script setup>
import {inject, onMounted, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import { taskStatusColors, convertToTitleCase } from '@/services/utils/global.js';
import { cloneDeep } from 'lodash';
import { computed } from 'vue';
import writeDentalPrescriptionHelper from '@/services/utils/pdf/writeDentalPrescriptionHelper';
import handleCompany from "@/services/modules/company";
import handlePurchase from "@/services/modules/purchase";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');

const props = defineProps({
    patientId: {
        type: [Number, String, null],
        default: null
    },
    serialNo: {
        type: [String, Number, null],
        default: null
    },
    doctorId: {
        type: [String, Number, null],
        default: null
    }
})

const { fetchDentalScheduleList, fetchSingleDentalSchedule, fetchSingleDoctorOrder } = handleDentalOs();
const { generatePdf } = writeDentalPrescriptionHelper();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const { fetchSalesOrderById } = handlePurchase();

const loading = ref(false)
const tableItems = ref([]);
const params = ref({
  offset: 20,
});
const data = ref(null);
const companyInfo = ref({});
const leftSideNotes = ref([]);
const rightSideNotes = ref([]);
const service = ref(null);
const latestOrder = ref(null);
const salesOrder = ref(null);
const customizationPdfFields = ref([]);

const isDoctor = computed(() => store.getters['user/isDoctor']);

const goToPrescription = async (item, type, withCustomization = false) => {

    loading.value = true;
    try {
        const res = await fetchSingleDentalSchedule(getQuery(), item.id);
        if (!res.status) return showError(res.message);

        let response = res.data;

        response.task_id = item.task_id || null;

        if (type === 'print'){
            return onClickToPrint(response, withCustomization)
        }

        if (response.type == 'treatment_plan'){
            onClickToCreatePrescription(response)
        }

        if (response.type == 'dental_appointment'){
            onClickToClose(response)
        }

    } finally {
        loading.value = false;
    }
}

const onClickToPrint = async (item, withCustomization = false) => {
    const query = getPrintQuery(item);
    const serviceId = item.service_general_id;
    let salesOrderId = null;

    const isHideFooter = ! withCustomization;
    const isHideHeader = ! withCustomization;

    loading.value = true;

    await getActiveFiledCustomization();

    try {
        const res = await fetchSingleDoctorOrder(query, serviceId);
        if (!res.status) return showError(res.message)
        data.value = res.data;
        setDoctorOrderData();
        salesOrderId = data.value.bill && data.value.bill.ipd_register ? data.value.bill.ipd_register.sales_order_id : null;

        const salesOrderRes = await fetchSalesOrderById(salesOrderId, query);
        if (salesOrderRes.status) {
            salesOrder.value = salesOrderRes.data;
        }
        
    } catch (error) {
        console.log(error);
    }

    await generatePdf(
        companyInfo.value,
        leftSideNotes.value,
        rightSideNotes.value,
        service.value,
        isHideHeader,
        isHideFooter,
        salesOrder.value,
        customizationPdfFields.value
    );

    loading.value = false;
}

const getActiveFiledCustomization = async () => {
    const companyQuery = `?company_id=${route.params.companyId}&customization_type=pdf&type=dental_prescription_pdf`;

    try {
        const res = await fetchActiveCustomizationField(companyQuery);
        customizationPdfFields.value = res.data;
    } catch (error) {
        return
    }
}

const setDoctorOrderData = () => {

    leftSideNotes.value = data.value.prescription_details.left_side_notes;
    rightSideNotes.value = data.value.prescription_details.right_side_notes;
    latestOrder.value = data.value.latest_prescription ? data.value.latest_prescription.doctor_order : null;
    service.value = data.value.bill;

    const doctorOrder = service.value.doctor_order;

    if(! doctorOrder) return;

    if(doctorOrder.investigation_orders.length){
        setInvestigationOrderData(doctorOrder.investigation_orders, 'investigation');
    }

    if(doctorOrder.medicine_orders.length){
        setMedicineOrderData(doctorOrder.medicine_orders);
    }

    if(doctorOrder.notes.length){
        setNoteData(doctorOrder.notes);
    }
}

const setMedicineOrderData = (medicineRx) => {
    
    let rxNote = rightSideNotes.value.find(item => item.value === 'rx');
    if(!rxNote) return;
    
    let formattedMedicineRx = formatMedicineRx(medicineRx);
    rxNote.selectedNotes = formattedMedicineRx;
}

const formatMedicineRx = (medicineRx) => {
    return medicineRx.map((medicine) => {
        let item = {
            id: medicine.id,
            product_id: medicine.product_id,
            service_resource_id: medicine.service_resource,
            name: medicine.product.name,
            remarks: medicine.remarks,
            is_selected: true,
            is_edited: true,
            dose: medicine.dose,
            unit: medicine.unit,
            duration: medicine.duration,
            madication_status: medicine.madication_status,
            is_published: medicine.is_published,
            group_index: medicine.group_index,
            order_date: medicine.order_date,
            created_at: medicine.created_at,
            status: medicine.status
        };

        return item;
    })
}

const setInvestigationOrderData = (medicineRx, noteType) => {

    let prevNote = leftSideNotes.value.find(item => item.value === noteType);
    if(!prevNote) return;

    for(let note of medicineRx){

        const noteObj = {
            id: note.id,
            product_id: note.product_id,
            name: note.product.name,
            remarks: note.remarks,
            is_selected: true,
            status: 'active',
            is_published: note.is_published,
        };

        prevNote.selectedNotes.push(noteObj)
    }
}

const setNoteData = (notes) => {
    const leftSideNoteKeys = leftSideNotes.value.reduce((acc, item, index) => {
                    acc[item.value] = index;
                    return acc;
                }, {});

    const rightSideNoteKeys = rightSideNotes.value.reduce((acc, item, index) => {
                acc[item.value] = index;
                return acc;
            }, {});

    notes.forEach((noteItem) => {
        
        const noteIndex = rightSideNoteKeys[noteItem.type];

        if(noteIndex !== undefined){
            rightSideNotes.value[noteIndex].id = noteItem.id;
            rightSideNotes.value[noteIndex].selectedNotes = noteItem.note;
            rightSideNotes.value[noteIndex].markedPositions = noteItem.marked_positions;
            return;
        }

        const leftSideNoteIndex = leftSideNoteKeys[noteItem.type];
        
        if(leftSideNoteIndex !== undefined){
            leftSideNotes.value[leftSideNoteIndex].id = noteItem.id;
            leftSideNotes.value[leftSideNoteIndex].selectedNotes = noteItem.note;
            leftSideNotes.value[leftSideNoteIndex].markedPositions = noteItem.marked_positions;
        }
    });
}

const getCompanyInfo = async () => {
    await fetchCompanyInfo(route.params.companyId).then((res) => {
        companyInfo.value = res.data
    })
}

const getPrintQuery = (item) => {
    let query = '?company_id=' + route.params.companyId;
    query += `&sales_order_general_id=${item.activitylogable_id}`
    return query;
}

const onClickToCreatePrescription = (item) => {

  const redirect = {
    name: 'dental-service',
    params: {
        ...route.params,
        serviceId: item.service_general_id
    },
    query: {
        ...route.query,
        general_id: item.sale_order_general_id,
        ipd_register_id: item.ipd_register_id,
        contact_id: item.ipd_contact_profile_id,
        service_status: item.status === 'completed' ? 'completed_service' : 'ongoing_service',
        task_id: item.task_id,
        from: 'list',
        back_url: encodeURIComponent(route.fullPath)
    }
  };

  router.push(redirect)
}

const onClickToClose = (response) => {
    const redirectObj = {
        name: 'dental-service',
        params: {
            ...route.params,
            serviceId: response.activitylogable_id
        },
        query: {
            ...route.query,
            service_status: "completed_service",
            contact_id: response.ipd_contact_profile_id,
            ipd_register_id: response.ipd_register_id,
            general_id: response.sales_order_general_id,
            from: 'list',
            back_url: encodeURIComponent(route.fullPath)
        }
    };

    if (! response.sales_order_general_id){
        redirectObj.query = {
            ...route.query,
            back_url: encodeURIComponent(route.fullPath)
        };
    }
    
    router.push(redirectObj)
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
};

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getDentalScheduleList();
}

const getDentalScheduleList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchDentalScheduleList(query).then(res => {
        if (!res.status){
            store.commit("resetPagination")
            return
        }
        setTableItem(res.data.data);
        setPagination(res.data)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const setTableItem = (data) => {
    tableItems.value = [];

    for (const item of data) {

        if (!item.tasks || !item.tasks.length) {
            tableItems.value.push(item);
            continue;
        }

        for (const task of item.tasks) {
            const copyItem = cloneDeep(item);
            copyItem.doctor_name = task.assigned_to;
            copyItem.product_name = task.activities;
            copyItem.start_time = task.start_time;
            copyItem.task_id = task.id;
            tableItems.value.push(copyItem);
        }
    }
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.page) query += '&page=' + route.query.page;
    if (route.query.start) query += '&start_date=' + route.query.start;
    if (route.query.end) query += '&end_date=' + route.query.end;
    if (props.doctorId) query += '&contact_profile_id=' + props.doctorId;
    if (props.patientId) query += '&patient_id=' + props.patientId;
    if (props.serialNo) query += '&serial_no=' + props.serialNo;
    query += '&offset=' + params.value.offset
    query += '&show_task=1';
    query += '&service_status=ongoing_service';
    return query;
}

onMounted(async () => {
   await getDentalScheduleList()
   getCompanyInfo();
})

defineExpose({
    getDentalScheduleList
})


</script>

<style scoped>
.table-responsive {
    overflow: visible;
}
.dropdown-menu.show {
    z-index: 1055;
}
</style>
